class DrawerMenu extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this;
    this.mainLevel = this.el.querySelector('[data-contents]');
    this.init();
    this.setupLazyHydration();
  }

  // TE speed (Oct 24): submenu panel contents ship inside <template data-te-lazy>; hydrate on first interaction
  setupLazyHydration() {
    if (!this.el.querySelector('template[data-te-lazy]')) return;
    this.hydrate = this.hydrate.bind(this);
    this.hydrateEvents = ['pointerdown', 'touchstart', 'keydown', 'focusin', 'mouseover'];
    this.hydrateEvents.forEach((type) => document.addEventListener(type, this.hydrate, { passive: true, capture: true }));
  }

  hydrate() {
    if (this.hydrateEvents) {
      this.hydrateEvents.forEach((type) => document.removeEventListener(type, this.hydrate, { capture: true }));
      this.hydrateEvents = null;
    }
    this.el.querySelectorAll('template[data-te-lazy]').forEach((tpl) => {
      const parent = tpl.parentNode;
      tpl.replaceWith(tpl.content.cloneNode(true));
      this.bindWithin(parent);
      if (typeof Accordion !== 'undefined') parent.querySelectorAll('details').forEach((d) => new Accordion(d));
    });
  }

  bindWithin(root) {
    root.querySelectorAll('[data-drawer-submenu-trigger]').forEach((trigger) => {
      if (trigger.teBound) return; trigger.teBound = true;
      trigger.addEventListener('click', this.onSubmenuTriggerClick.bind(this));
    });
    root.querySelectorAll('[data-close]').forEach((btn) => {
      if (btn.teBound) return; btn.teBound = true;
      btn.addEventListener('click', () => this.resetSubmenus());
    });
    root.querySelectorAll('[data-drawer-submenu-close]').forEach((button) => {
      if (button.teBound) return; button.teBound = true;
      button.addEventListener('click', this.onSubmenuBackClick.bind(this));
    });
  }

  init() {
    this.bindWithin(this.el);

    this.overlay = this.el.querySelector('[data-overlay]');
    this.overlay.addEventListener('click', () => this.resetSubmenus());

    this.subitemOpeners = document.querySelectorAll('[data-drawer-submenu-open]');
    this.subitemOpeners.forEach((button) => {
      button.addEventListener('click', this.toggleMenuItemFromTheme.bind(this));
    });
  }

  resetSubmenus() {
    this.closeAllSubmenusExcept(null);
  }

  closeAllSubmenusExcept(skipAccordion) {
    this.mainLevel.querySelectorAll('[data-accordion-item].is-open').forEach((accordion) => {
      if (skipAccordion && accordion === skipAccordion) {
        return;
      }
      accordion.classList.remove('is-open');
      const trigger = accordion.querySelector('[data-drawer-submenu-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    if (!skipAccordion) {
      this.mainLevel.classList.remove('has-open-submenu');
    }
  }

  setSubmenuOpen(accordion, open) {
    if (!accordion) return;

    const trigger = accordion.querySelector('[data-drawer-submenu-trigger]');
    if (open) {
      accordion.classList.add('is-open');
      this.mainLevel.classList.add('has-open-submenu');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    } else {
      accordion.classList.remove('is-open');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (!this.mainLevel.querySelector('[data-accordion-item].is-open')) {
        this.mainLevel.classList.remove('has-open-submenu');
      }
    }
  }

  openSubmenu(accordion) {
    if (!accordion) return;

    const isOpen = accordion.classList.contains('is-open');
    if (isOpen) {
      this.setSubmenuOpen(accordion, false);
      return;
    }

    this.closeAllSubmenusExcept(accordion);
    this.setSubmenuOpen(accordion, true);
  }

  ensureSubmenuOpen(accordion) {
    if (!accordion || accordion.classList.contains('is-open')) return;

    this.closeAllSubmenusExcept(accordion);
    this.setSubmenuOpen(accordion, true);
  }

  onSubmenuTriggerClick(event) {
    const trigger = event.currentTarget;
    const accordion = trigger.closest('[data-accordion-item]');
    this.openSubmenu(accordion);
  }

  toggleMenuItemFromTheme(scope) {
    const button = scope.currentTarget || scope;
    const submenuId = button.getAttribute('data-drawer-submenu-open');
    if (!submenuId) return;
    if (this.hydrateEvents) this.hydrate();

    const accordion = this.el.querySelector(
      `[data-accordion-item][data-submenu-id="${submenuId}"]`
    );
    if (!accordion) return;

    Woolman.ModalsAndDrawers.showModalOrDrawer(this.el.getAttribute('id'));

    const animationFinished = () => {
      this.el.removeEventListener('transitionend', animationFinished);
      this.ensureSubmenuOpen(accordion);
    };
    this.el.addEventListener('transitionend', animationFinished);
  }

  onSubmenuBackClick(event) {
    this.resetSubmenus();

    const button = event.currentTarget;
    const accordionId = button.getAttribute('data-drawer-submenu-close');
    if (!accordionId) return;

    const panel = document.getElementById(accordionId + '-container');
    const accordion = panel?.closest('[data-accordion-item]');
    const trigger = accordion?.querySelector('[data-drawer-submenu-trigger]');
    if (trigger) {
      trigger.focus();
    }
  }
}

if (customElements.get('drawer-menu') === undefined) {
  customElements.define('drawer-menu', DrawerMenu);
}
